/**
 * ReleaseDrawerHost
 *
 * The ONLY place that renders the Release drawer. Mounted once in DashboardLayout, driven by
 * releaseDrawer.store (open() from anywhere).
 *
 * Responsibilities:
 * - URL intake (input only, never mirrored): `/approvals?postId=&variationId=&tab=` and, on any route,
 *   `?release=<postId>&releaseVariation=&releaseTab=`. The params are stripped with `replace`.
 * - Close on route change (unless the change came from the intake) and on an effective team change
 *   (unless a pending target is waiting for exactly that team: the brand switch below).
 * - Brand mismatch: the API answers for any brand the viewer may access (D9), but Edit, mentions and
 *   context belong to the effective brand, so we ask to switch first (enter/exit God Mode, double refresh).
 * - Missing post and no access show the SAME "not available" copy (no existence leak).
 *
 * Access is decided by the server only; nothing here grants access client-side.
 */

import { lazy, Suspense, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { isAxiosError } from 'axios';
import { useQuery } from '@tanstack/react-query';
import { approvalService } from '@/services/approval.service';
import { Button } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';
import { Icon } from '@/components/ui/Icon';
import { ICONS } from '@/constants/icons';
import { useReleaseDetail } from '@/hooks/useApproval';
import { useEffectiveTeamId } from '@/hooks/useEffectiveTeamId';
import { useAuth } from '@/hooks/useAuth';
import { agencyService, exitGodModeSafely } from '@/services/agency.service';
import { formatApiError, setAccessToken } from '@/services/api';
import {
  useReleaseDrawerStore,
  type ReleaseDrawerTab,
  type ReleaseDrawerTarget,
} from '@/stores/releaseDrawer.store';
import { toast } from '@/utils/toast';

const ReleaseDrawer = lazy(() => import('./ReleaseDrawer'));

const TABS: readonly ReleaseDrawerTab[] = ['comments', 'details', 'history'];

const parseId = (raw: string | null): number | null =>
  raw !== null && /^\d+$/.test(raw) && Number(raw) > 0 ? Number(raw) : null;

const parseTab = (raw: string | null): ReleaseDrawerTab | undefined =>
  TABS.find((tab) => tab === raw);

interface PendingTarget {
  teamId: number;
  target: ReleaseDrawerTarget;
}

export const ReleaseDrawerHost = () => {
  const { t } = useTranslation('common');
  const location = useLocation();
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();
  const { teamId: effectiveTeamId, hasTeam } = useEffectiveTeamId();
  const target = useReleaseDrawerStore((s) => s.target);
  const token = useReleaseDrawerStore((s) => s.token);
  const open = useReleaseDrawerStore((s) => s.open);
  const close = useReleaseDrawerStore((s) => s.close);
  const [isSwitching, setIsSwitching] = useState(false);

  // Target waiting for a brand switch to finish (the team-change effect opens it instead of closing).
  const pendingRef = useRef<PendingTarget | null>(null);
  // Pathname at which the URL intake opened the drawer: that navigation must not close it.
  const intakePathRef = useRef<string | null>(null);
  const prevPathRef = useRef(location.pathname);
  const prevTeamRef = useRef<number | null>(effectiveTeamId);

  // --- URL intake ------------------------------------------------------------------------------------
  // Both link kinds (`?release=` anywhere, `/approvals?postId=`) are consumed and stripped in ONE pass, valid or
  // not, keeping hash and router state. `release` wins when both are present (one drawer, never two).
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const onBoard = location.pathname === '/approvals';
    const hasRelease = params.has('release');
    const hasBoardPost = onBoard && params.has('postId');
    if (!hasRelease && !hasBoardPost) return;

    const release = parseId(params.get('release'));
    const boardPost = hasBoardPost ? parseId(params.get('postId')) : null;
    const viaRelease = release !== null;
    const postId = release ?? boardPost;
    if (postId !== null) {
      const variationId = parseId(params.get(viaRelease ? 'releaseVariation' : 'variationId'));
      const tab = parseTab(params.get(viaRelease ? 'releaseTab' : 'tab'));
      intakePathRef.current = location.pathname;
      open({ postId, variationId, tab, source: 'link' });
    }

    ['release', 'releaseVariation', 'releaseTab'].forEach((key) => params.delete(key));
    if (hasBoardPost) ['postId', 'variationId', 'tab'].forEach((key) => params.delete(key));
    const search = params.toString();
    navigate(
      { pathname: location.pathname, search: search ? `?${search}` : '', hash: location.hash },
      { replace: true, state: location.state }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, location.search]);

  // --- Close on route change (not when the intake caused it) ------------------------------------------
  useEffect(() => {
    if (prevPathRef.current === location.pathname) return;
    prevPathRef.current = location.pathname;
    // The intake exemption is for this one navigation only.
    const fromIntake = intakePathRef.current === location.pathname;
    intakePathRef.current = null;
    if (fromIntake) return;
    if (useReleaseDrawerStore.getState().target) close();
  }, [location.pathname, close]);

  // --- Team change: open the pending target, else close -----------------------------------------------
  useEffect(() => {
    const previous = prevTeamRef.current;
    prevTeamRef.current = effectiveTeamId;
    const pending = pendingRef.current;
    if (previous === null && pending && pending.teamId === effectiveTeamId) {
      // Portfolio staff left "no team" through the brand switch: the target is still set and now loads normally.
      pendingRef.current = null;
      return;
    }
    if (previous === null || previous === effectiveTeamId) return;
    if (pending && pending.teamId === effectiveTeamId) {
      pendingRef.current = null;
      open(pending.target);
      return;
    }
    if (useReleaseDrawerStore.getState().target) close();
  }, [effectiveTeamId, open, close]);

  // A switch that did not land (no team change after it finished) must not leave a stale pending target.
  useEffect(() => {
    const pending = pendingRef.current;
    if (!isSwitching && pending && effectiveTeamId !== pending.teamId) pendingRef.current = null;
  }, [isSwitching, effectiveTeamId]);

  // --- Brand check + access (shared cache with the drawer's own query) --------------------------------
  // Always the Original: a missing version must not read as "post not available" (the drawer falls back).
  const detailQuery = useReleaseDetail(target?.postId ?? 0, null, target !== null);
  const status = isAxiosError(detailQuery.error) ? detailQuery.error.response?.status : undefined;
  // No effective team (Brand Portfolio staff outside God Mode): every drawer query is disabled, so resolve the
  // post's brand directly (same endpoint the old board path used). An accessible brand offers the switch; a
  // missing or inaccessible post gets the same "not available" copy (no existence leak).
  const brandLookup = useQuery({
    queryKey: ['release-drawer', 'post-brand', target?.postId ?? 0],
    queryFn: () => approvalService.getPostBrand(target?.postId ?? 0),
    enabled: target !== null && !hasTeam,
    retry: false,
    staleTime: 0,
    gcTime: 0,
  });
  const notAvailable =
    target !== null &&
    (!hasTeam ? brandLookup.isError : (status === 404 || status === 403) && !detailQuery.data);
  const brand =
    target !== null && hasTeam && detailQuery.data && !detailQuery.isPlaceholderData ? detailQuery.data.brand : null;
  const mismatch: { id: number; name: string } | null =
    target !== null && !hasTeam && brandLookup.data
      ? { id: brandLookup.data.teamId, name: brandLookup.data.teamName }
      : brand !== null && effectiveTeamId !== null && brand.id !== effectiveTeamId
        ? brand
        : null;
  const lookingUpBrand = target !== null && !hasTeam && brandLookup.isLoading;

  const handleSwitch = async (): Promise<void> => {
    if (!target || !mismatch || isSwitching) return;
    setIsSwitching(true);
    pendingRef.current = { teamId: mismatch.id, target };
    try {
      if (user?.teamId === mismatch.id) {
        await exitGodModeSafely();
      } else {
        const res = await agencyService.enterGodMode(mismatch.id);
        setAccessToken(res.accessToken);
      }
      try {
        await refreshUser();
      } catch {
        // The token already switched; resync once more so the UI follows it.
        await refreshUser();
      }
    } catch (err) {
      pendingRef.current = null;
      toast.error(formatApiError(err).message);
    } finally {
      setIsSwitching(false);
    }
  };

  const handleDismissPrompt = (): void => {
    pendingRef.current = null;
    close();
  };

  if (!target || lookingUpBrand) return null;

  if (notAvailable || mismatch) {
    // Shared Modal keeps focus, Escape, and stack behavior consistent with other dialogs.
    return (
      <Modal
        open
        onOpenChange={(isOpen) => {
          if (!isOpen) handleDismissPrompt();
        }}
        size="sm"
        title={mismatch ? t('release_other_brand_title') : t('release_not_available_title')}
        preventClose={isSwitching}
        showCloseButton={false}
      >
        <div className="flex flex-col gap-5">
          <div className="flex items-start gap-3">
            <span className="grid h-[32px] w-[32px] flex-none place-items-center rounded-full bg-primary-50 text-primary-700" aria-hidden="true">
              <Icon name={mismatch ? ICONS.ARROW_RIGHT : ICONS.CAUTION} size="custom" className="text-[16px]" ariaHidden />
            </span>
            <p className="pt-1 text-sm leading-5 text-gray-700">
              {mismatch
                ? t('release_other_brand_prompt', { brand: mismatch.name })
                : t('release_other_brand_no_access')}
            </p>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="secondary" size="sm" className="min-h-[44px] sm:min-h-0" onClick={handleDismissPrompt} disabled={isSwitching}>
              {t('release_other_brand_dismiss')}
            </Button>
            {mismatch && (
              <Button variant="primary" size="sm" className="min-h-[44px] sm:min-h-0" onClick={() => void handleSwitch()} disabled={isSwitching}>
                {t('release_other_brand_switch')}
              </Button>
            )}
          </div>
        </div>
      </Modal>
    );
  }

  return (
    <Suspense fallback={null}>
      <ReleaseDrawer
        key={`${token}-${target.postId}`}
        postId={target.postId}
        initialVariationId={target.variationId}
        initialTab={target.tab}
        openInChangesMode={target.openInChangesMode}
        source={target.source}
        isOpen
        onClose={close}
      />
    </Suspense>
  );
};
